import { X } from 'lucide-react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { getFriendlyErrorMessage } from '../../utils/friendlyErrors';

/**
 * Format phone number for display
 * Supports US format (10 digits) and international format (starts with +)
 * 
 * US format: (503) 555-1234
 * International format: +1 503 555 1234 (preserves + prefix)
 */
function formatPhoneNumber(input: string): string {
  const digits = input.replace(/\D/g, '');
  const hasPlus = input.trim().startsWith('+');
  
  // If starts with +, treat as international
  if (hasPlus) {
    // International format: +1 503 555 1234
    // Keep + prefix and format with spaces
    if (digits.length === 0) return '+';
    if (digits.length <= 1) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0, 1)} ${digits.slice(1)}`;
    if (digits.length <= 7) return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4)}`;
    if (digits.length <= 11) {
      return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    // For numbers longer than 11 digits (extensions)
    return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)} ${digits.slice(11)}`;
  }
  
  // US format: (503) 555-1234 (default for 10-digit numbers)
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  // Cap at 10 digits for US format
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  // If more than 10 digits without +, treat as international without + prefix
  // Format as: 1 503 555 1234 (user can add + manually)
  if (digits.length <= 11) {
    return `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)} ${digits.slice(11)}`;
}

/**
 * Extract digits from phone number for storage/validation
 */
function extractPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * Validate phone number format
 * Returns true if phone is empty (optional) or has valid format (10 digits for US, or valid international)
 */
function validatePhoneNumber(phone: string): boolean {
  if (!phone.trim()) return true; // Optional field
  const digits = extractPhoneDigits(phone);
  const hasPlus = phone.trim().startsWith('+');
  
  // US format: exactly 10 digits
  if (!hasPlus && digits.length === 10) return true;
  
  // International format: 11 digits with + (country code + number)
  if (hasPlus && digits.length >= 10 && digits.length <= 15) return true;
  
  return false;
}

interface DirectInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function DirectInviteModal({ isOpen, onClose, onSuccess }: DirectInviteModalProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();
  // The gym the admin is actually looking at — last_selected_company, falling
  // back to the JWT claim. NOT an arbitrary membership row; see handleInvite.
  const { data: companyId } = useCurrentCompany();

  const handleInvite = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    // Validate phone number if provided
    if (phone.trim() && !validatePhoneNumber(phone)) {
      setError('Please enter a valid phone number (10 digits for US, or international format starting with +).');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      // The gym must be the one the admin has selected, not "whichever
      // membership row Postgres returns first".
      //
      // This read `user_company_relationships … .limit(1).single()` with no
      // ORDER BY and no reference to the selected company. For an admin who
      // belongs to more than one gym that is an arbitrary row, and it does not
      // change when they switch gyms — so invites went out branded for, and
      // filed under, the wrong gym while the whole UI said otherwise. The
      // edge function trusts this id (it looks the company up by id and never
      // checks the caller is a member), so the client picking wrong is the
      // whole bug.
      if (!companyId) throw new Error('Company not found');

      const { data, error: invokeError } = await supabase.functions.invoke('send-direct-invite', {
        body: {
          email: trimmedEmail,
          companyId,
          adminUserId: currentUser.id,
          firstName: firstName.trim() || null,
          lastName: lastName.trim() || null,
          phoneNumber: phone.trim() ? extractPhoneDigits(phone) : null,
        },
      });

      if (invokeError) throw invokeError;

      if (data?.success) {
        showToast(data.message || `Invitation sent to ${trimmedEmail}`, 'success');
        handleClose();
        onSuccess?.();
        return;
      }

      throw new Error(data?.error || 'invite_failed');
    } catch (err: unknown) {
      console.error('Error sending direct invite:', err);
      setError(getFriendlyErrorMessage(err, 'invite'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPhone('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-text-primary">Direct Invite</h2>
          <button
            onClick={handleClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-text-secondary mb-4">Send an invitation to someone new</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              maxLength={254}
              className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-text-primary placeholder:text-[#808080] focus:outline-none focus:border-[#9B1421]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              First Name (optional)
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First Name"
              maxLength={30}
              className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-text-primary placeholder:text-[#808080] focus:outline-none focus:border-[#9B1421]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Last Name (optional)
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last Name"
              maxLength={30}
              className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-text-primary placeholder:text-[#808080] focus:outline-none focus:border-[#9B1421]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Phone (optional)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value);
                setPhone(formatted);
                // Clear error when user starts typing
                if (error && error.includes('phone')) {
                  setError(null);
                }
              }}
              placeholder="(503) 555-1234"
              maxLength={20}
              className={`w-full px-3 py-2 bg-[#363232] border rounded text-text-primary placeholder:text-[#808080] focus:outline-none focus:border-[#9B1421] ${
                phone.trim() && !validatePhoneNumber(phone)
                  ? 'border-red-500'
                  : 'border-[#504A4A]'
              }`}
            />
            {phone.trim() && !validatePhoneNumber(phone) && (
              <p className="text-red-400 text-xs mt-1">
                Please enter a valid phone number (10 digits for US, or international format starting with +)
              </p>
            )}
          </div>
          
          {error && (
            <div className="text-red-400 text-sm">{error}</div>
          )}
          
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-text-primary border border-[#504A4A]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={loading || !email.trim()}
              className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-text-primary"
            >
              {loading ? 'Sending...' : 'Send Invite'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal using portal to escape sidebar constraints
  return createPortal(modalContent, document.body);
}
